import { RitualVisibilityEnum } from "../types"

export interface PrivacyOption {
  value: string
  label: string
}

export interface PrivacySettingConfig {
  options: PrivacyOption[]
  defaultSelected: string[]
  disabledOptions: string[]
  autoSelected: string[]
  groupExclusion?: {
    group1: string[]
    group2: string[]
  }
  selectAllWith?: Record<string, string[]>
}

export interface PrivacyConfig {
  join: PrivacySettingConfig
  invite: PrivacySettingConfig
  comment: PrivacySettingConfig
  edit: PrivacySettingConfig
  seeParticipantList: PrivacySettingConfig
}

export const WHO_CAN_JOIN_OPTIONS: PrivacyOption[] = [
  { value: "everyone", label: "Everyone" },
  { value: "followers", label: "Followers" },
  { value: "community_members", label: "Community members" },
  { value: "invite_req", label: "Invite / request to join" },
]

export const WHO_CAN_INVITE_OPTIONS: PrivacyOption[] = [
  { value: "owner", label: "Owner" },
  { value: "organizers", label: "Organizers" },
  { value: "participants", label: "Participants" },
]

export const WHO_CAN_COMMENT_OPTIONS: PrivacyOption[] = [
  { value: "owner", label: "Owner" },
  { value: "organizers", label: "Organizers" },
  { value: "participants", label: "Participants" },
  { value: "anyone", label: "Anyone" },
]

export const WHO_CAN_EDIT_OPTIONS: PrivacyOption[] = [
  { value: "owner", label: "Owner" },
  { value: "organizers", label: "Organizers" },
]

export const WHO_CAN_SEE_PARTICIPANT_LIST_OPTIONS: PrivacyOption[] = [
  { value: "owner", label: "Owner" },
  { value: "organizers", label: "Organizers" },
  { value: "participants", label: "Participants" },
  { value: "everyone", label: "Everyone" },
]

const JOIN_GROUP_1 = ["everyone", "followers", "community_members"]
const JOIN_GROUP_2 = ["invite_req"]

export const PRIVACY_CONFIG_BY_VISIBILITY: Record<RitualVisibilityEnum, PrivacyConfig> = {
  [RitualVisibilityEnum.PUBLIC]: {
    join: {
      options: WHO_CAN_JOIN_OPTIONS,
      defaultSelected: ["everyone"],
      disabledOptions: [],
      autoSelected: [],
      groupExclusion: {
        group1: JOIN_GROUP_1,
        group2: JOIN_GROUP_2,
      },
    },
    invite: {
      options: WHO_CAN_INVITE_OPTIONS,
      defaultSelected: ["owner"],
      disabledOptions: ["owner"],
      autoSelected: ["owner"],
    },
    comment: {
      options: WHO_CAN_COMMENT_OPTIONS,
      defaultSelected: ["owner"],
      disabledOptions: ["owner"],
      autoSelected: ["owner"],
    },
    edit: {
      options: WHO_CAN_EDIT_OPTIONS,
      defaultSelected: ["owner"],
      disabledOptions: ["owner"],
      autoSelected: ["owner"],
    },
    seeParticipantList: {
      options: WHO_CAN_SEE_PARTICIPANT_LIST_OPTIONS,
      defaultSelected: ["owner"],
      disabledOptions: ["owner"],
      autoSelected: ["owner"],
      selectAllWith: {
        everyone: ["owner", "organizers", "participants"],
      },
    },
  },
  [RitualVisibilityEnum.PERSONAL]: {
    join: {
      options: WHO_CAN_JOIN_OPTIONS,
      defaultSelected: ["invite_req"],
      disabledOptions: ["everyone", "followers", "community_members", "invite_req"],
      autoSelected: ["invite_req"],
      groupExclusion: {
        group1: JOIN_GROUP_1,
        group2: JOIN_GROUP_2,
      },
    },
    invite: {
      options: WHO_CAN_INVITE_OPTIONS,
      defaultSelected: ["owner"],
      disabledOptions: ["owner", "organizers", "participants"],
      autoSelected: ["owner"],
    },
    comment: {
      options: WHO_CAN_COMMENT_OPTIONS,
      defaultSelected: ["owner"],
      disabledOptions: ["owner", "organizers", "participants", "anyone"],
      autoSelected: ["owner"],
    },
    edit: {
      options: WHO_CAN_EDIT_OPTIONS,
      defaultSelected: ["owner"],
      disabledOptions: ["owner", "organizers"],
      autoSelected: ["owner"],
    },
    seeParticipantList: {
      options: WHO_CAN_SEE_PARTICIPANT_LIST_OPTIONS,
      defaultSelected: ["owner"],
      disabledOptions: ["owner", "organizers", "participants", "everyone"],
      autoSelected: ["owner"],
    },
  },
  [RitualVisibilityEnum.PRIVATE]: {
    join: {
      options: WHO_CAN_JOIN_OPTIONS,
      defaultSelected: ["invite_req"],
      disabledOptions: ["everyone"],
      autoSelected: ["invite_req"],
      groupExclusion: {
        group1: JOIN_GROUP_1,
        group2: JOIN_GROUP_2,
      },
    },
    invite: {
      options: WHO_CAN_INVITE_OPTIONS,
      defaultSelected: ["owner"],
      disabledOptions: ["owner"],
      autoSelected: ["owner"],
    },
    comment: {
      options: WHO_CAN_COMMENT_OPTIONS,
      defaultSelected: ["owner"],
      disabledOptions: ["owner", "anyone"],
      autoSelected: ["owner"],
    },
    edit: {
      options: WHO_CAN_EDIT_OPTIONS,
      defaultSelected: ["owner"],
      disabledOptions: ["owner"],
      autoSelected: ["owner"],
    },
    seeParticipantList: {
      options: WHO_CAN_SEE_PARTICIPANT_LIST_OPTIONS,
      defaultSelected: ["owner"],
      disabledOptions: ["owner", "everyone"],
      autoSelected: ["owner"],
      selectAllWith: {
        everyone: ["owner", "organizers", "participants"],
      },
    },
  },
}

export function getPrivacyConfig(visibility: RitualVisibilityEnum): PrivacyConfig {
  return (
    PRIVACY_CONFIG_BY_VISIBILITY[visibility] ||
    PRIVACY_CONFIG_BY_VISIBILITY[RitualVisibilityEnum.PUBLIC]
  )
}

export function getDeselectedValuesForVisibility(
  visibility: RitualVisibilityEnum,
  currentSelections: {
    join?: string[]
    comment?: string[]
    invite?: string[]
    edit?: string[]
    seeParticipantList?: string[]
  },
): {
  join: string[]
  comment: string[]
  invite: string[]
  edit: string[]
  seeParticipantList: string[]
} {
  const config = getPrivacyConfig(visibility)

  const deselectAndFilter = (current: string[] = [], cfg: PrivacySettingConfig): string[] => {
    const autoSelected = cfg.autoSelected
    const disabled = cfg.disabledOptions
    const withoutDisabled = current.filter(v => !disabled.includes(v))
    const withAuto = [...new Set([...autoSelected, ...withoutDisabled])]
    return withAuto
  }

  const handleJoinForPrivate = (current: string[] = []): string[] => {
    if (visibility === RitualVisibilityEnum.PRIVATE) {
      const filteredGroup1 = current.filter(v => !JOIN_GROUP_1.includes(v))
      const hasGroup2Selected = filteredGroup1.some(v => JOIN_GROUP_2.includes(v))
      if (hasGroup2Selected) {
        return filteredGroup1
      }
      return ["invite_req"]
    }
    return deselectAndFilter(current, config.join)
  }

  return {
    join: handleJoinForPrivate(currentSelections.join),
    comment: deselectAndFilter(currentSelections.comment, config.comment),
    invite: deselectAndFilter(currentSelections.invite, config.invite),
    edit: deselectAndFilter(currentSelections.edit, config.edit),
    seeParticipantList: deselectAndFilter(
      currentSelections.seeParticipantList,
      config.seeParticipantList,
    ),
  }
}
